// Last-known-good snapshots in localStorage so the wall shows something
// useful instantly after a reload or while offline. Every read is guarded:
// storage can be unavailable (private mode, kiosk policies) and the app must
// still work without it.

const PREFIX = "km:cache:";
const MAX_BYTES = 400_000;

export interface Snapshot<T> {
  data: T;
  savedAt: number;
}

export function readSnapshot<T>(key: string): Snapshot<T> | undefined {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw ? (JSON.parse(raw) as Snapshot<T>) : undefined;
  } catch {
    return undefined;
  }
}

export function writeSnapshot<T>(key: string, data: T, savedAt: number): void {
  try {
    const raw = JSON.stringify({ data, savedAt } satisfies Snapshot<T>);
    if (raw.length > MAX_BYTES) return;
    localStorage.setItem(PREFIX + key, raw);
  } catch {
    /* quota or disabled storage: snapshots are a convenience only */
  }
}

export function clearSnapshots(): void {
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith(PREFIX)) localStorage.removeItem(k);
  } catch {
    /* ignore */
  }
}

export function readPref<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`km:pref:${key}`);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writePref<T>(key: string, value: T): void {
  try {
    localStorage.setItem(`km:pref:${key}`, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}
